import {AddLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useLexicalEditable} from '@lexical/react/useLexicalEditable';
import {
  $computeTableMapSkipCellCheck,
  $createTableCellNode,
  $createTableRowNode,
  $isTableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {usePlanningTableReserveRight} from 'components/DSYS/editor/PlanningTableReserveContext';
import {updateTablePositioning} from 'components/DSYS/editor/plugins/TableCellResizer';
import {LexicalEditor, NodeKey} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings';
import {ReactPortal, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {TableAddRowColumnButton} from './TableAddRowColumnButton';
import {useTableButtonPositioning} from './useTableButtonPositioning';
import {useTableDeleteHandler} from './useTableDeleteHandler';
import {useTableDragToAdd} from './useTableDragToAdd';
import {useTablePasteHandler} from './useTablePasteHandler';
import {useTableSelectionHandlers} from './useTableSelectionHandlers';
import {DEFAULT_COLUMN_WIDTH} from './utils';

type TableInfo = {
  tableElement: HTMLElement;
  tableNode: TableNode;
  tableKey: NodeKey;
  enableAddRows: boolean;
  enableAddColumns: boolean;
};

const TableAddRowColumnButtons = ({
  editor,
  anchorElem,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
}) => {
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null);
  const isTemplateMode = useIsTemplateMode();
  const reserveRightPx = usePlanningTableReserveRight();
  const reserveRightRef = useRef(reserveRightPx);
  reserveRightRef.current = reserveRightPx;

  useTablePasteHandler(editor);
  useTableDeleteHandler(editor);

  const {handleColumnClick, handleRowClick} = useTableSelectionHandlers(
    editor,
    tableInfo,
    setTableInfo,
    () => {} // setSelectedCellKeys not needed in main component
  );

  const {addColumnButtonStyle, addRowButtonStyle, columnHandleStyles, rowHandleStyles} =
    useTableButtonPositioning(tableInfo, isTemplateMode, anchorElem);

  const handleAddColumn = useCallback(() => {
    if (!tableInfo) return;

    editor.update(() => {
      const tableNode = editor.getEditorState()._nodeMap.get(tableInfo.tableKey) as TableNode;
      if (!tableNode) return;

      const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
      const numRows = tableMap.length;

      // Add a new cell to each existing row, copying styles from the same row
      for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
        const rowNode = tableNode.getChildAtIndex(rowIndex) as TableRowNode;
        if (rowNode) {
          // Get a reference cell from the same row to copy styles
          const referenceCellInfo = tableMap[rowIndex]?.[0]; // Use first cell in the row
          const referenceCell = referenceCellInfo?.cell;

          let newCell: ReturnType<typeof $createTableCellNode>;
          if (referenceCell && $isTableCellNode(referenceCell)) {
            // Copy header styles and background color from reference cell
            const headerStyles = referenceCell.getHeaderStyles();
            newCell = $createTableCellNode(headerStyles);

            const backgroundColor = referenceCell.getBackgroundColor();
            if (backgroundColor) {
              newCell.setBackgroundColor(backgroundColor);
            }
          } else {
            newCell = $createTableCellNode(0);
          }

          rowNode.append(newCell);
        }
      }

      // Update column widths to include new column
      const colWidths = tableNode.getColWidths();
      if (colWidths) {
        const newColWidths = [...colWidths, DEFAULT_COLUMN_WIDTH];
        tableNode.setColWidths(newColWidths);

        const totalWidth = newColWidths.reduce((sum, w) => sum + w, 0);
        tableNode.setStyle(`width: ${totalWidth}px;`);

        updateTablePositioning(tableNode, editor, reserveRightRef.current);
      }
    });
  }, [editor, tableInfo]);

  const handleAddRow = useCallback(() => {
    if (!tableInfo) return;

    editor.update(() => {
      const tableNode = editor.getEditorState()._nodeMap.get(tableInfo.tableKey) as TableNode;
      if (!tableNode) return;

      const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
      const numColumns = tableMap[0]?.length || 0;

      // Create new row with correct number of cells
      const newRow = $createTableRowNode();
      for (let i = 0; i < numColumns; i++) {
        newRow.append($createTableCellNode(0));
      }

      tableNode.append(newRow);
    });
  }, [editor, tableInfo]);

  const {handleAddColumnDragStart, handleAddRowDragStart} = useTableDragToAdd(
    tableInfo,
    handleAddRow,
    handleAddColumn
  );

  if (!tableInfo || !addColumnButtonStyle || !addRowButtonStyle) {
    return null;
  }

  return (
    <>
      {(isTemplateMode || tableInfo.enableAddColumns) && (
        <>
          {/* Column handles at the top */}
          {columnHandleStyles.map((style, index) => (
            <TableAddRowColumnButton
              key={`col-${index}`}
              onClick={(e) => handleColumnClick(index, e.shiftKey)}
              style={style}
            />
          ))}
          <TableAddRowColumnButton
            src={AddLine}
            onClick={() => handleAddColumn()}
            onMouseDownCapture={handleAddColumnDragStart}
            style={addColumnButtonStyle}
          />
        </>
      )}

      {(isTemplateMode || tableInfo.enableAddRows) && (
        <>
          {/* Row handles on the left */}
          {rowHandleStyles.map((style, index) => (
            <TableAddRowColumnButton
              key={`row-${index}`}
              onClick={(e) => handleRowClick(index, e.shiftKey)}
              style={style}
            />
          ))}
          <TableAddRowColumnButton
            src={AddLine}
            onClick={() => handleAddRow()}
            onMouseDownCapture={handleAddRowDragStart}
            style={addRowButtonStyle}
          />
        </>
      )}
    </>
  );
};

const TableAddRowColumnPlugin = (): null | ReactPortal => {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [anchorElem, setAnchorElem] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Get the editor's root element and use its parent as the anchor
    // The parent should have position: relative for absolute positioning to work
    const rootElement = editor.getRootElement();
    if (rootElement) {
      // Use the root element's parent which should be a positioned container
      const anchor = rootElement.parentElement;
      if (anchor) {
        setAnchorElem(anchor);
      }
    }
  }, [editor]);

  if (!isEditable || !anchorElem) {
    return null;
  }

  return createPortal(
    <TableAddRowColumnButtons editor={editor} anchorElem={anchorElem} />,
    anchorElem
  );
};

export default TableAddRowColumnPlugin;
